<template>
  <NuxtImg
    v-slot="{ src, isLoaded, imgAttrs }"
    :src="image.src"
    :alt="image.altText"
    :width="width"
    :height="height"
    :sizes="width || height ? undefined : 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw'"
    :provider="image.src.startsWith('http') ? 'umbraco' : null"
    :format="image.src.endsWith('.svg') ? undefined : 'webp'"
    :custom="true"
    :loading="lazy ? 'lazy' : undefined"
    :preload="lazy ? false : { fetchPriority: 'high' }">
    <div
      class="relative w-full h-full overflow-hidden"
      :style="{ aspectRatio: !!aspectRatio && withAspectRatio ? aspectRatio : undefined }"
      :class="$attrs.class">
      <div class="absolute inset-0 bg-theme-30 transition-opacity duration-300" :class="{ 'opacity-0': isLoaded }" />
      <img
        v-if="isLoaded"
        class="w-full h-full"
        :alt="image.altText"
        :class="[
          {
            'object-cover': fit === 'cover',
            'object-contain': fit === 'contain',
          },
          imageClass,
        ]"
        :style="coverPosition"
        v-bind="imgAttrs"
        :fetchpriority="lazy ? undefined : 'high'"
        :src="src" >
    </div>
  </NuxtImg>
</template>

<script setup lang="ts">
// import type { Image as ImageType } from '@gro-up/types'
import type { ImageApiModel } from '@gro-up/api/corporate'
import { computed } from 'vue'

// Without width, height or a `sizes` attribute NuxtImg serves the CMS original (up to 6000px wide).
// A full-width `sizes` is the fallback; a caller's own `sizes` falls through to NuxtImg and wins.
// Keep the breakpoint syntax: a bare "100vw" makes @nuxt/image emit 1px and 2px widths.
export type ImageProps = {
  image: ImageApiModel
  fit?: 'cover' | 'contain'
  width?: number
  height?: number
  lazy?: boolean
  withAspectRatio?: boolean
  imageClass?: string
}

const props = withDefaults(defineProps<ImageProps>(), {
  fit: 'cover',
  lazy: true,
  width: undefined,
  height: undefined,
  withAspectRatio: false,
  imageClass: undefined,
})

const aspectRatio = computed(() => props.image.width ?? 1 / (props.image.height ?? 1))

const coverPosition = computed(() => {
  if (props.fit !== 'cover' || !props.image.focalPointX || !props.image.focalPointY) {
    return {}
  }

  const x = props.image.focalPointX * 100
  const y = props.image.focalPointY * 100

  return {
    objectPosition: `${x}% ${y}%`,
  }
})
</script>
